/* eslint-disable react/no-unused-prop-types */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import forIn from 'lodash/forIn';
import videojs from 'video.js';
import defaultOptions from './defaultOptions';
import { preparePluginOptions, initializePlugin } from './util/plugin';
import enableKeyboardShortcuts from './util/aria-tabbed';
import moveButtonAfter from './util/move-videojs-button';

videojs.log.level('off');

export default class BNNVARAVideoPlayer extends Component {
  static propTypes = {
    // HTML identifier which will be appended with a unique id.
    id: PropTypes.string,
    // The media ID
    mediaId: PropTypes.string.isRequired,
    // The initial poster to show.
    poster: PropTypes.string,
    // The initial poster to show.
    loadingPosterImage: PropTypes.string,
    // The initial source to show.
    source: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        src: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        ratio: PropTypes.string.isRequired,
      })
    ),
    // The title of the video.
    title: PropTypes.string,
    // Whether the player should be playing.
    play: PropTypes.bool,
    // Marks the player as a vertical.
    vertical: PropTypes.bool,
    // Default width of the video.
    width: PropTypes.number,
    // Default height of the video.
    height: PropTypes.number,
    // Whether or not the player is running on mobile
    isMobile: PropTypes.bool,
    // The plugins to load for this player
    // `imports` should be an array of external modules to load
    // `mapPropStateToOptions` can either be an object with options or a function
    // that resolves to an options object.
    plugins: PropTypes.objectOf(
      PropTypes.shape({
        mapPropStateToOptions: PropTypes.oneOfType([
          PropTypes.func,
          PropTypes.object,
        ]),
      })
    ),
    // Subtitle tracks to load with the video.
    subtitles: PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string.isRequired,
        language: PropTypes.string.isRequired,
        srcLang: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ),
    genres: PropTypes.arrayOf(PropTypes.string),
    broadcasters: PropTypes.string,
  };

  static defaultProps = {
    id: 'bnnvara-video',
    poster: null,
    loadingPosterImage: null,
    source: [],
    title: null,
    play: false,
    vertical: false,
    width: 560,
    height: 315,
    isMobile: false,
    plugins: {},
    subtitles: [],
    genres: [],
    broadcasters: null,
  };

  constructor(props) {
    super(props);

    const { id } = this.props;

    this.state = {
      // A unique ID for the HTML element using the id prop as a prefix.
      nodeId: uniqueId(id),
    };
  }

  componentDidMount() {
    this.updatePlayer();
  }

  componentDidUpdate(prevProps) {
    this.updatePlayer(prevProps);
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }
  }

  // HTML5 video element reference.
  node;

  // video.js
  player;

  async updatePlayer(prevProps) {
    const pluginOptions = await this.preparePlugins();
    this.updateVideoJsInstance(prevProps, pluginOptions);
  }

  preparePlugins() {
    const { plugins } = this.props;
    const loaders = [];

    forIn(plugins, (config, name) => {
      loaders.push(
        preparePluginOptions({
          name,
          config,
          player: this.player,
          props: this.props,
          state: this.state,
        })
      );
    });

    return Promise.all(loaders);
  }

  /**
   * Makes sure the plugin configuration that is passed to
   * this component are initialized.
   */
  initializePlugins(pluginOptions) {
    pluginOptions.forEach(({ name, options }) =>
      initializePlugin({
        player: this.player,
        name,
        options,
      })
    );
  }

  createVideoJsInstance(pluginOptions) {
    const { play } = this.props;

    this.player = videojs(
      this.videoNode,
      {
        ...defaultOptions,
        autoplay: play,
      },
      () => {
        // Some plug-ins require initialization when the player is ready
        this.initializePlugins(pluginOptions);

        // Enable (accessibility) keyboard shortcuts
        enableKeyboardShortcuts(this.player);

        // Move Airplay button to the front
        moveButtonAfter({
          sourceName: 'AirplayToggle',
          afterName: 'CustomControlSpacer',
          player: this.player,
        });

        this.player.on('error', () => {
          // @todo this still triggers the ads to play first
          this.player.src({
            type: 'video/mp4',
            src: process.env.REACT_APP_FALLBACK_VIDEO,
          });

          this.player.play();
        });
      }
    );

    // Some plug-ins require an immediate initialization
    this.initializePlugins(pluginOptions);
  }

  /**
   * Updates the internal video.js instance based on the props.
   *
   * @param prevProps
   */
  updateVideoJsInstance(prevProps = {}, pluginOptions) {
    const { source, poster, play, subtitles, isMobile } = this.props;

    if (source.length && prevProps.source !== source) {
      if (!this.player) {
        this.createVideoJsInstance(pluginOptions);
      }

      this.player.src(source);
    }

    if (this.player) {
      if (prevProps.poster !== poster) {
        this.player.poster(poster);
      }

      if (prevProps.subtitles !== subtitles) {
        if (Array.isArray(subtitles)) {
          subtitles.forEach(subtitle =>
            this.player.addRemoteTextTrack(
              {
                ...subtitle,
                default:
                  isMobile && subtitle.language === this.player.language(),
              },
              true
            )
          );
        }
      }

      // Toggles the player's `playing` state
      if (play) {
        this.player.play();
      } else {
        this.player.pause();
      }
    }
  }

  render() {
    const { loadingPosterImage } = this.props;
    const { nodeId } = this.state;

    return (
      <div data-vjs-player>
        <video
          ref={node => {
            this.videoNode = node;
          }}
          id={nodeId}
          poster={loadingPosterImage}
          preload="metadata"
          className="video-js"
          style={{ background: '#000', width: '100%', height: '100%' }}
          playsInline
        />
      </div>
    );
  }
}
